import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
   FaReact,
   FaNodeJs,
   FaAws,
   FaDocker,
   FaPython,
   FaJava,
   FaHtml5,
   FaCode,
   FaComments,
   FaCss3,
   FaGithub,
   FaUsers,
   FaSyncAlt, 
   FaLightbulb,
   FaRegClock,
} from 'react-icons/fa';
import {
   SiJavascript,
   SiMongodb,
   SiAdobephotoshop,
   SiAdobeillustrator,
   SiSpringboot,
   SiIntellijidea,
   SiPostman,
   SiJira,
   SiConfluence,
   SiFirebase,
} from 'react-icons/si';
import { GrMysql } from 'react-icons/gr';
import { BiLogoPostgresql, BiLogoVisualStudio } from 'react-icons/bi';
import { MdDesignServices } from 'react-icons/md';
import { RiTailwindCssFill } from "react-icons/ri";
import { FaHandFist } from "react-icons/fa6";

const SkillCategory = ({ category, index }) => {
   const { ref, inView } = useInView({
      threshold: 0.2,
      triggerOnce: true
   });

   return (
      <motion.div
         ref={ref}
         initial={{ opacity: 0, y: 20 }}
         animate={inView ? { opacity: 1, y: 0 } : {}}
         transition={{ duration: 0.6, delay: index * 0.2 }}
         className="border-b border-gray-200 pb-8 w-full max-w-xs"
      >
         <h4 className="text-lg font-light text-gray-900 mb-4">
            {category.title}
         </h4>
         <ul className="flex flex-col gap-2">
            {category.skills.map((skill, skillIndex) => (
               <li
                  key={skillIndex}
                  className={`flex items-center gap-2 text-lg text-gray-600 ${skill.color} transition-colors duration-500`}
               >
                  <skill.icon className={`w-5 h-5`} />
                  <span className={`text-gray-600`}>
                     {skill.name}
                  </span>
               </li>
            ))}
         </ul>
      </motion.div>
   );
};

const Skills = () => {
   const skillCategories = [
      {
         title: 'Frontend',
         skills: [
            { name: 'React', icon: FaReact, color: 'hover:text-cyan-500' },
            { name: 'TailwindCSS', icon: RiTailwindCssFill, color: 'hover:text-cyan-500' },
            { name: 'HTML', icon: FaHtml5, color: 'hover:text-red-500' },
            { name: 'CSS', icon: FaCss3, color: 'hover:text-blue-500' },
            { name: 'JavaScript', icon: SiJavascript, color: 'hover:text-yellow-400' },
         ],
      },
      {
         title: 'Backend',
         skills: [
            { name: 'Node.js', icon: FaNodeJs, color: 'hover:text-green-600' },
            { name: 'Spring Boot', icon: SiSpringboot, color: 'hover:text-green-700' },
         ],
      },
      {
         title: 'Languages',
         skills: [
            { name: 'Java', icon: FaJava, color: 'hover:text-red-600' },
            { name: 'Python', icon: FaPython, color: 'hover:text-blue-600' },
            { name: 'C', icon: FaCode, color: 'hover:text-blue-500' },
         ],
      },
      {
         title: 'Databases',
         skills: [
            { name: 'PostgreSQL', icon: BiLogoPostgresql, color: 'hover:text-blue-600' },
            { name: 'MongoDB', icon: SiMongodb, color: 'hover:text-green-600' },
            { name: 'MySQL', icon: GrMysql, color: 'hover:text-blue-700' },
            { name: 'Firebase', icon: SiFirebase, color: 'hover:text-orange-400' },
         ],
      },
      {
         title: 'DevOps & Cloud',
         skills: [
            { name: 'AWS', icon: FaAws, color: 'hover:text-orange-500' },
            { name: 'Docker', icon: FaDocker, color: 'hover:text-blue-500' },
         ],
      },
      {
         title: 'Design & UI/UX',
         skills: [
            { name: 'UI/UX', icon: MdDesignServices, color: 'hover:text-pink-400' },
            { name: 'Photoshop', icon: SiAdobephotoshop, color: 'hover:text-blue-600' },
            { name: 'Illustrator', icon: SiAdobeillustrator, color: 'hover:text-orange-500' },
         ],
      },
      {
         title: 'Tools',
         skills: [
            { name: 'VS Code', icon: BiLogoVisualStudio, color: 'hover:text-blue-500' },
            { name: 'IntelliJ IDEA', icon: SiIntellijidea, color: 'hover:text-purple-600' },
            { name: 'Postman', icon: SiPostman, color: 'hover:text-orange-500' },
            { name: 'Jira', icon: SiJira, color: 'hover:text-blue-600' },
            { name: 'Confluence', icon: SiConfluence, color: 'hover:text-sky-600' },
            { name: 'GitHub', icon: FaGithub, color: 'hover:text-gray-700' },
         ],
      },
      {
         title: 'Soft Skills',
         skills: [
            { name: 'Leadership', icon: FaHandFist, color: 'hover:text-red-600' },
            { name: 'Communication', icon: FaComments, color: 'hover:text-rose-500' },
            { name: 'Problem Solving & Critical Thinking', icon: FaLightbulb, color: 'hover:text-amber-500' },
            { name: 'Teamwork', icon: FaUsers, color: 'hover:text-indigo-500' },
            { name: 'Adaptability', icon: FaSyncAlt, color: 'hover:text-yellow-500' },
            { name: 'Time Management', icon: FaRegClock, color: 'hover:text-violet-500' },
         ],
      },
   ];

   return (
      <section className="mb-48 px-4 sm:px-6 lg:px-8">
         <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-20 text-center">
               <h3 className="text-xs text-gray-500 font-medium mb-4 uppercase tracking-wider">
                  Technical Capacity
               </h3>
               <h2 className="text-4xl font-light text-gray-900">
                  Essential Tools
               </h2>
            </div>

            {/* Skill Categories */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center">
               {skillCategories.map((category, index) => (
                  <SkillCategory key={index} category={category} index={index} />
               ))}
            </div>
         </div>
      </section>
   );
};

export default Skills;